import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PencilSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { appPath } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useUrlTab } from '@/hooks/use-linked-record';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { EmployeeProfileSkeleton } from '../../components/directory-skeletons/directory-skeletons';
import {
  EmployeeProfileView,
  ProfileTab,
} from '../../components/employee-profile-view/employee-profile-view';
import {
  AddressCard,
  AttendanceOvertimeCard,
  BasicInfoCard,
  EducationCard,
  EmergencyContactCard,
  EmploymentCard,
  ExpertiseCard,
  IdentityDocumentsCard,
  InsuranceCard,
  KpisCard,
  LeaveAllotmentCard,
  PersonalDetailsCard,
  PrivateContactCard,
  showsPrivateInfo,
} from '../../components/employee-profile-view/profile-cards';
import { ProfileCardGrid } from '../../components/employee-profile-view/profile-section-card';
import { currentTermOf } from '../../components/employee-profile-view/profile-terms';
import {
  EDIT_TABS,
  EditTab,
  EMPLOYEE_DETAIL_TABS,
  EmployeeDetailTab,
} from '../../constants/profile-tabs';
import { useGetEmployeeById } from '../../hooks/use-employees';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';

const { OVERVIEW, ATTENDANCE, LEAVE, SKILLS } = EMPLOYEE_DETAIL_TABS;

/**
 * `/people/:employeeId` (Component Map › Employee details, Option A): the header with the HR term and
 * Edit, the summary rail, and the tabs Overview / Attendance & Overtime / Leave / Skills & KPIs. Attendance
 * & Overtime and Leave show only to those who may open Team Attendance and Team Time Off (2 Nov 2026).
 * Each card's pencil opens the edit page on its section's tab.
 */
export const EmployeeDetailsPage = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();
  const canUpdate = useHasPermission(HRM_PERMISSIONS.PEOPLE.UPDATE);
  const canViewAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TEAM_ATTENDANCE);
  const canViewLeave = useHasPermission(HRM_PERMISSIONS.TIME_OFF.VIEW_TEAM);

  const tabs: ProfileTab<EmployeeDetailTab>[] = [
    { value: OVERVIEW, label: t('OVERVIEW') },
    ...(canViewAttendance ? [{ value: ATTENDANCE, label: t('ATTENDANCE_AND_OVERTIME') }] : []),
    ...(canViewLeave ? [{ value: LEAVE, label: t('LEAVE') }] : []),
    { value: SKILLS, label: t('SKILLS_AND_KPIS') },
  ] as ProfileTab<EmployeeDetailTab>[];
  const [tab, setTab] = useUrlTab(
    tabs.map((item) => item.value),
    OVERVIEW
  );

  const { data, isLoading } = useGetEmployeeById(employeeId);
  const employee: Employee | undefined = (data as { Employees?: EmployeesPagingResult })?.Employees
    ?.items?.[0];
  // The HR term the term-based tabs show: the current one unless another is picked.
  const [pickedTermId, setPickedTermId] = useState<string>();

  if (isLoading) return <EmployeeProfileSkeleton />;
  if (!employee) {
    return <p className="text-sm text-muted-foreground">{t('EMPLOYEE_NOT_FOUND')}</p>;
  }

  const term =
    employee.HrTerms.find((item) => item.ItemId === pickedTermId) ?? currentTermOf(employee);
  const editTo = (section: EditTab) =>
    canUpdate ? `${appPath.personEdit(employee.ItemId)}?tab=${section}` : undefined;
  const showPrivate = showsPrivateInfo(employee, false);
  const card = { employee };

  const actions = (
    <>
      {employee.HrTerms.length > 1 && term && (
        <Select value={term.ItemId} onValueChange={setPickedTermId}>
          <SelectTrigger className="w-auto min-w-48" aria-label={t('HR_TERM_PICKER')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...employee.HrTerms].reverse().map((item) => (
              <SelectItem key={item.ItemId} value={item.ItemId}>
                {t('HR_TERM_OPTION', {
                  period: item.HRPeriod,
                  start: formatDateOnly(item.HrPeriodStartDate),
                  end: formatDateOnly(item.HrPeriodEndDate),
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {canUpdate && (
        <Button asChild>
          <Link to={appPath.personEdit(employee.ItemId)}>
            <PencilSimple />
            {t('EDIT')}
          </Link>
        </Button>
      )}
    </>
  );

  const noTerm = <p className="text-sm text-muted-foreground">{t('NO_CURRENT_HR_TERM')}</p>;

  return (
    <EmployeeProfileView
      employee={employee}
      actions={actions}
      tabs={tabs}
      tab={tab}
      onTabChange={setTab}
    >
      {tab === OVERVIEW && (
        <ProfileCardGrid>
          <EmploymentCard {...card} editTo={editTo(EDIT_TABS.OVERVIEW)} />
          <BasicInfoCard {...card} editTo={editTo(EDIT_TABS.OVERVIEW)} />
          {showPrivate && <PrivateContactCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />}
          <PersonalDetailsCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <IdentityDocumentsCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <EmergencyContactCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          {showPrivate && <AddressCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />}
          <EducationCard {...card} editTo={editTo(EDIT_TABS.CONTACT)} />
          <InsuranceCard {...card} editTo={editTo(EDIT_TABS.OVERVIEW)} />
        </ProfileCardGrid>
      )}
      {tab === ATTENDANCE &&
        (term ? (
          <AttendanceOvertimeCard {...card} term={term} editTo={editTo(EDIT_TABS.HR)} />
        ) : (
          noTerm
        ))}
      {tab === LEAVE &&
        (term ? (
          <LeaveAllotmentCard
            {...card}
            term={term}
            title={t('LEAVE_ALLOCATIONS')}
            editTo={editTo(EDIT_TABS.HR)}
          />
        ) : (
          noTerm
        ))}
      {tab === SKILLS && (
        <ProfileCardGrid>
          {/* Only the person edits their skills (Edit profile); HR has no skills form. */}
          <ExpertiseCard {...card} />
          <KpisCard />
        </ProfileCardGrid>
      )}
    </EmployeeProfileView>
  );
};
